import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { chatService } from '../services/ChatService.js'
import Events from '@/events/events'

/**
 * Composable principal pour la gestion du chat
 * Offre une interface réactive pour interagir avec le service de chat
 */
export function useChat(config = {}) {
  // État réactif
  const isConnected = ref(false)
  const currentConversation = ref(null)
  const conversations = ref([])
  const messages = ref([])
  const typingUsers = ref(new Set())
  const isLoading = ref(false)
  const error = ref(null)

  // État pour l'input
  const inputText = ref('')
  const isTyping = ref(false)

  // Configuration
  const chatConfig = reactive({
    ...chatService.config,
    ...config
  })

  // Handlers d'événements
  const eventHandlers = new Map()

  /**
   * Initialise le composable
   */
  const init = () => {
    // S'abonner aux événements du service de chat
    bindEvents()

    // Récupérer l'état initial
    isConnected.value = chatService.isConnectedToChat()
    conversations.value = chatService.getConversations()
    currentConversation.value = chatService.getCurrentConversation()

    // Si déjà connecté, charger les messages de la conversation actuelle
    if (currentConversation.value) {
      loadMessages(currentConversation.value.id)
    }
  }

  /**
   * Lie les événements du service de chat
   */
  const bindEvents = () => {
    // Connexion
    const onChatReady = () => {
      isConnected.value = true
      error.value = null
    }

    const onChatError = ({ error: err }) => {
      error.value = err
      console.error('Chat error:', err)
    }

    // Conversations
    const onConversationJoined = ({ conversation }) => {
      const existingIndex = conversations.value.findIndex(c => c.id === conversation.id)
      if (existingIndex >= 0) {
        conversations.value[existingIndex] = conversation
      } else {
        conversations.value.push(conversation)
      }

      if (currentConversation.value?.id === conversation.id) {
        currentConversation.value = conversation
      }
    }

    const onConversationLeft = ({ conversationId }) => {
      conversations.value = conversations.value.filter(c => c.id !== conversationId)
      if (currentConversation.value?.id === conversationId) {
        currentConversation.value = null
        messages.value = []
      }
    }

    const onConversationUpdated = ({ conversation }) => {
      const index = conversations.value.findIndex(c => c.id === conversation.id)
      if (index >= 0) {
        conversations.value[index] = conversation
      }
      if (currentConversation.value?.id === conversation.id) {
        currentConversation.value = conversation
      }
    }

    const onTyping = ({ userId, conversationId, isTyping: typing }) => {
      if (conversationId !== currentConversation.value?.id) return

      if (typing) {
        typingUsers.value.add(userId)
      } else {
        typingUsers.value.delete(userId)
      }
    }

    // Messages
    const onMessageNew = ({ message, conversationId }) => {
      if (conversationId !== currentConversation.value?.id) return

      // Ajouter le message si pas déjà présent
      const existingIndex = messages.value.findIndex(m => m.id === message.id)
      if (existingIndex < 0) {
        messages.value.push(message)
        sortMessages()
      }

      // Marquer comme lu si message d'un autre utilisateur
      if (message.userId !== chatService.currentUser?.id) {
        markAsRead(conversationId, message.id)
      }
    }

    const onMessageUpdated = ({ message, conversationId }) => {
      if (conversationId !== currentConversation.value?.id) return

      const index = messages.value.findIndex(m => m.id === message.id)
      if (index >= 0) {
        messages.value[index] = message
      }
    }

    const onMessageDeleted = ({ messageId, conversationId }) => {
      if (conversationId !== currentConversation.value?.id) return

      messages.value = messages.value.filter(m => m.id !== messageId)
    }

    // Enregistrer les handlers
    Events.handle('chat:ready', onChatReady)
    Events.handle('chat:error', onChatError)
    Events.handle('conversation:joined', onConversationJoined)
    Events.handle('conversation:left', onConversationLeft)
    Events.handle('conversation:updated', onConversationUpdated)
    Events.handle('conversation:typing', onTyping)
    Events.handle('message:new', onMessageNew)
    Events.handle('message:updated', onMessageUpdated)
    Events.handle('message:deleted', onMessageDeleted)

    // Garder une référence pour le nettoyage
    eventHandlers.set('chat:ready', onChatReady)
    eventHandlers.set('chat:error', onChatError)
    eventHandlers.set('conversation:joined', onConversationJoined)
    eventHandlers.set('conversation:left', onConversationLeft)
    eventHandlers.set('conversation:updated', onConversationUpdated)
    eventHandlers.set('conversation:typing', onTyping)
    eventHandlers.set('message:new', onMessageNew)
    eventHandlers.set('message:updated', onMessageUpdated)
    eventHandlers.set('message:deleted', onMessageDeleted)
  }

  /**
   * Nettoie les abonnements aux événements
   */
  const cleanup = () => {
    eventHandlers.forEach((handler, event) => {
      // Note: Adapté selon votre système d'événements
      // Events.off(event, handler)
    })
    eventHandlers.clear()
  }

  /**
   * Crée une nouvelle conversation
   */
  const createConversation = async (participants, type = 'direct', metadata = {}) => {
    try {
      isLoading.value = true
      error.value = null

      const conversation = await chatService.createConversation(participants, type, metadata)
      return conversation
    } catch (err) {
      error.value = err
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Rejoint une conversation existante
   */
  const joinConversation = async (conversationId) => {
    try {
      isLoading.value = true
      error.value = null

      await chatService.joinConversation(conversationId)
      await loadMessages(conversationId)
    } catch (err) {
      error.value = err
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Quitte une conversation
   */
  const leaveConversation = async (conversationId) => {
    try {
      isLoading.value = true
      error.value = null

      await chatService.leaveConversation(conversationId)
    } catch (err) {
      error.value = err
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Charge les messages d'une conversation
   */
  const loadMessages = async (conversationId, limit = 50) => {
    try {
      isLoading.value = true
      error.value = null

      const fetchedMessages = await chatService.fetchMessages(conversationId, limit)

      // Fusionner avec les messages existants
      const existingIds = new Set(messages.value.map(m => m.id))
      const newMessages = fetchedMessages.filter(m => !existingIds.has(m.id))

      messages.value = [...messages.value, ...newMessages]
      sortMessages()

      return fetchedMessages
    } catch (err) {
      error.value = err
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Envoie un message
   */
  const sendMessage = async (text, attachments = [], replyTo = null) => {
    if (!currentConversation.value) {
      throw new Error('No active conversation')
    }

    try {
      error.value = null
      const message = await chatService.sendMessage(
        currentConversation.value.id,
        text,
        attachments,
        replyTo
      )

      // Vider l'input après envoi réussi
      inputText.value = ''

      return message
    } catch (err) {
      error.value = err
      throw err
    }
  }

  /**
   * Marque un message comme lu
   */
  const markAsRead = async (conversationId, messageId) => {
    try {
      await chatService.markAsRead(conversationId, messageId)
    } catch (err) {
      console.error('Failed to mark message as read:', err)
    }
  }

  /**
   * Gère l'indicateur de typing
   */
  const handleTypingStart = () => {
    if (!currentConversation.value || !inputText.value.trim()) return

    if (!isTyping.value) {
      isTyping.value = true
      chatService.startTyping(currentConversation.value.id)
    }
  }

  const handleTypingStop = () => {
    if (isTyping.value && currentConversation.value) {
      isTyping.value = false
      chatService.stopTyping(currentConversation.value.id)
    }
  }

  /**
   * Trie les messages par date
   */
  const sortMessages = () => {
    messages.value.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
  }

  // Computed
  const currentUser = computed(() => chatService.currentUser)
  const hasMessages = computed(() => messages.value.length > 0)
  const typingIndicator = computed(() => {
    const users = Array.from(typingUsers.value)
      .filter(userId => userId !== currentUser.value?.id)
      .map(userId => {
        const conversation = currentConversation.value
        const user = conversation?.participants?.find(p => p.id === userId)
        return user?.name || 'Someone'
      })

    if (users.length === 0) return null
    if (users.length === 1) return `${users[0]} is typing...`
    return `${users.slice(0, -1).join(', ')} and ${users[users.length - 1]} are typing...`
  })

  const unreadCount = computed(() => {
    return conversations.value.reduce((total, conv) => total + (conv.unreadCount || 0), 0)
  })

  // Cycle de vie
  onMounted(() => {
    init()
  })

  onUnmounted(() => {
    cleanup()
  })

  return {
    // État
    isConnected,
    currentConversation,
    conversations,
    messages,
    typingUsers,
    isLoading,
    error,
    inputText,
    isTyping,
    chatConfig,

    // Computed
    currentUser,
    hasMessages,
    typingIndicator,
    unreadCount,

    // Méthodes
    createConversation,
    joinConversation,
    leaveConversation,
    loadMessages,
    sendMessage,
    markAsRead,
    handleTypingStart,
    handleTypingStop
  }
}